/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, Dispatch } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { WidgetTypes } from "@swiggy-private/dls-react-mxn/dist/WidgetController/constants";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { QrCodeActions, PinGenerationStatus } from "@swiggy-private/portal-dineout-context";
import type { IQrCodeAction } from "@swiggy-private/portal-dineout-context";
import {
    getCartItemFromLocalCart,
    type IMenuCategoryGrid,
    type IMenuCategoryInfo,
} from "@swiggy-private/portal-dineout-ui";

import { getToastBottomHeightMenuPage, getToastStyle, getToastWidth } from "utils";
import { getShowBackFromStorage } from "./sessionFlags";
import {
    ICategoryItems,
    IITemCategoryType,
    INestedItemCategoryType,
    HandleOrderToastParams,
    WIDGET_TYPES,
} from "./types";
import { ROUTES } from "../constants";
import { DELAYED_TOAST_CONSTANTS, DELAYED_TOAST_IDS } from "../constants/delayedToast";
import { VEG_NONVEG_FILTERS_LIST } from "../constants/menu";

export const getItemCategoryCards = (widget: { itemCards: ICategoryItems[] }) => {
    return widget.itemCards;
};

export const getNestedItemCategories = (widget: INestedItemCategoryType) => {
    return widget.categories;
};

export const COLORS_TOKEN: Record<string, keyof IColors> = {
    TEXT_SELECTED: "text_inverse_Highest_Emphasis",
    TEXT_UNSELECTED: "text_High_Emphasis",
    BORDER_SELECTED: "border_Primary",
    BORDER_UNSELECTED: "border_Secondary",
    BG_SELECTED: "background_inverse_Primary",
    BG_UNSELECTED: "background_Primary",
    BG_HALF_CARD: "canvas_Primary",
};

export const getCollectionItemCount = (collectionItem: IITemCategoryType | INestedItemCategoryType) => {
    let numOfItems = 0;
    if (collectionItem["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE && getItemCategoryCards(collectionItem)) {
        numOfItems += getItemCategoryCards(collectionItem).length;
    }

    if (collectionItem["@type"] === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE && getNestedItemCategories(collectionItem)) {
        getNestedItemCategories(collectionItem).forEach((category) => {
            numOfItems += getItemCategoryCards(category).length;
        });
    }
    return numOfItems;
};

export const generateMenuCategoryWidgetData = (cards: any[], currentSelectedCategoryId: string | number = "") => {
    const menuCategoryWidget: IMenuCategoryGrid = {
        "@type": WIDGET_TYPES.MENU_CATEGORY_GRID,
        categoryList: [],
        currentSelectedCategoryId: currentSelectedCategoryId || "",
    };

    let pillIndex = -1;
    let menuWidgetIndex = -1;
    cards.forEach((card, index) => {
        const inner = card?.card?.card;
        if (!inner) return;
        const type = inner["@type"];
        if (type === WIDGET_TYPES.PILLS) {
            pillIndex = index;
        } else if (type === WIDGET_TYPES.MENU_CATEGORY_GRID) {
            menuWidgetIndex = index;
        } else if (type === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE || type === WIDGET_TYPES.ITEM_CATEGORY_TYPE) {
            const count = getCollectionItemCount(inner);
            if (count) {
                menuCategoryWidget.categoryList.push({
                    logo: {
                        url: inner.image,
                        height: 60,
                        width: 60,
                    },
                    name: inner.title,
                    count: count,
                    categoryId: inner.categoryId,
                    parentType: inner.parentType,
                });
            }
        }
    });

    const newCards = [...cards];
    if (menuWidgetIndex !== -1) {
        newCards[menuWidgetIndex] = {
            card: {
                card: menuCategoryWidget,
            },
        };
    } else if (pillIndex !== -1) {
        newCards.splice(pillIndex + 1, 0, {
            card: {
                card: menuCategoryWidget,
            },
        });
    } else {
        newCards.push({
            card: {
                card: menuCategoryWidget,
            },
        });
    }
    return newCards;
};

export const filterByParentType = (cards: any[], parentType: string) => {
    if (isEmpty(parentType)) return cards;
    return cards.filter((card) => {
        const inner = card?.card?.card;
        if (!inner) return true;
        if (
            inner["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE ||
            inner["@type"] === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE
        ) {
            return inner.parentType === parentType;
        }
        return true;
    });
};

export const filterDietOptions = (cards: any[], name: string) => {
    if (isEmpty(name) || isEmpty(VEG_NONVEG_FILTERS_LIST)) return cards;
    const filterName = name.trim().toLowerCase();
    return cards.filter((card) => {
        const type = card?.card?.card?.["@type"];
        if (!VEG_NONVEG_FILTERS_LIST.includes(filterName)) {
            return type !== WidgetTypes.MENU_VEG_FILTER_AND_BADGE;
        }
        return true;
    });
};

// Common transformation logic for pills styling
export const transformPillsAndGetSelectedTab = (cards: any[]) => {
    let selectedTab = "";

    cards.forEach((card: any) => {
        const cardObj = card.card.card;

        if (cardObj["@type"] === WIDGET_TYPES.PILLS) {
            const tabs = cardObj.tabs;

            tabs.forEach((tab: any) => {
                tab.selectedBackgroundColor = COLORS_TOKEN.BG_SELECTED;
                tab.unselectedBackgroundColor = COLORS_TOKEN.BG_UNSELECTED;
                tab.selectedTextColor = COLORS_TOKEN.TEXT_SELECTED;
                tab.unselectedTextColor = COLORS_TOKEN.TEXT_UNSELECTED;
                tab.selectedBorderColor = COLORS_TOKEN.BORDER_SELECTED;
                tab.unselectedBorderColor = COLORS_TOKEN.BORDER_UNSELECTED;
                tab.showRightIcon = false;
            });

            selectedTab = tabs[0].name;
        }
    });

    return selectedTab;
};

// Collections-specific transformer - includes necessary transformations but no category grid
export const collectionsTransformer = (data: any) => {
    if (isEmpty(data?.cards)) {
        return [];
    }

    const cards = data?.cards;

    // Transform pills styling and get selected tab
    const selectedTab = transformPillsAndGetSelectedTab(cards);

    // Filter diet options but don't generate category grid
    const collectionDataAfterDietOptions = filterDietOptions(cards, selectedTab);

    return collectionDataAfterDietOptions;
};

// Remap the mxn-owned RefineWidget type to a local custom type so we render our own implementation
const remapRefineWidgetType = (cards: IPLWidgetControllerCards[]): IPLWidgetControllerCards[] =>
    cards.map((card) => {
        const inner = card?.card?.card as unknown as Record<string, unknown>;
        if (inner?.["@type"] === WIDGET_TYPES.REFINE_WIDGET) {
            return {
                ...card,
                card: { ...card.card, card: { ...inner, "@type": WIDGET_TYPES.REFINE_WIDGET_CUSTOM } },
            } as unknown as IPLWidgetControllerCards;
        }
        return card;
    });

export const menuTransformer = (data: any) => {
    if (isEmpty(data?.cards)) {
        return [];
    }

    const cards = remapRefineWidgetType(data?.cards);

    // Transform pills styling and get selected tab (reusing common logic)
    const selectedTab = transformPillsAndGetSelectedTab(cards);

    // menuCategoryWidget which is to be appended to overall collection
    const finalData = generateMenuCategoryWidgetData(cards);
    // filter diet options
    const menuDataAfterDietOptions = filterDietOptions(finalData, selectedTab);

    return { menuDataAfterDietOptions, selectedTab };
};

const updateItemCardsWithCart = (itemCards: any[], localCart: any) => {
    return itemCards.map((item: any) => ({
        ...item,
        card: {
            ...item.card,
            info: {
                ...item.card.info,
                cartItem: getCartItemFromLocalCart(item.card.info.id, localCart),
            },
        },
    }));
};

export const updateMenuItemInDish = (cards: any, localCart: any) => {
    const collection = cards;
    const showBack = getShowBackFromStorage();
    collection.forEach((card: any) => {
        const cardObj = card.card.card;
        if (cardObj["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE) {
            if (cardObj.itemCards && Array.isArray(cardObj.itemCards)) {
                cardObj.itemCards = updateItemCardsWithCart(cardObj.itemCards, localCart);
            }
        }
        if (cardObj["@type"] === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE) {
            if (cardObj.categories && Array.isArray(cardObj.categories)) {
                cardObj.categories.forEach((category: any) => {
                    if (category.itemCards && Array.isArray(category.itemCards)) {
                        category.itemCards = updateItemCardsWithCart(category.itemCards, localCart);
                    }
                });
            }
        }
        if (cardObj["@type"] === WIDGET_TYPES.MENU_VEG_FILTER_AND_BADGE) {
            card.card.card = { ...cardObj, showOnlyVeg: true };
        }
        if (cardObj["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE) {
            card.card.card = {
                ...cardObj,
                showAccordion: !isEmpty(cardObj.title),
            };
        }
        if (cardObj["@type"] === WIDGET_TYPES.ENTITY_INFO_WITH_MASTHEAD) {
            card.card.card = {
                ...cardObj,
                showBack: showBack,
            };
        }
    });
    return collection;
};

export const processFilterCards = (itemCards: any[], preference: string) => {
    return itemCards.filter((itemCard) => {
        if (itemCard.card["@type"] === WIDGET_TYPES.DISH) {
            const vegClassifier = itemCard.card.info?.itemAttribute?.vegClassifier;
            if (vegClassifier === preference) {
                return true;
            }
        }
    });
};

export const deepClone = (obj: any) => {
    return JSON.parse(JSON.stringify(obj));
};

export const getFilteredCardsByVeg = (cards: any[], preference: string) => {
    if (isEmpty(preference) || preference === "DEFAULT") return cards;

    const pref = preference === "EGG" ? "VEG" : preference;

    return cards.map((cardObj) => {
        const newCard = { ...cardObj };
        const inner = newCard?.card?.card;

        if (
            inner &&
            (inner["@type"] === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE ||
                inner["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE)
        ) {
            const newInner = { ...inner };

            if (newInner.categories) {
                newInner.categories = newInner.categories.map((cat: any) => ({
                    ...cat,
                    itemCards: processFilterCards(cat.itemCards || [], pref),
                }));
            }

            if (newInner.itemCards) {
                newInner.itemCards = processFilterCards(newInner.itemCards, pref);
            }

            newCard.card = { card: newInner };
        }
        return newCard;
    });
};

export const isCollectionEmpty = (cards: Array<any>): boolean => {
    if (isEmpty(cards)) {
        return true;
    }

    const cardsClone = deepClone(cards);

    let cardFound = false;

    cardsClone.some((card: any) => {
        if (
            card.card.card["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE ||
            card.card.card["@type"] === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE
        ) {
            if (card.card.card.categories) {
                card.card.card.categories = card.card.card.categories.some((category: any) => {
                    if (category.itemCards.length > 0) {
                        cardFound = true;
                        return true;
                    }
                });
            }
            if (card.card.card.itemCards) {
                if (card.card.card.itemCards.length > 0) {
                    cardFound = true;
                    return true;
                }
            }
        }
    });
    return !cardFound;
};

export const filterByCategoryId = (cards: Array<any>, categoryId: any) => {
    if (isEmpty(categoryId)) {
        return cards;
    }
    // Filter cards by parentType
    return cards.filter((card: any) => {
        if (
            card.card.card["@type"] === WIDGET_TYPES.ITEM_CATEGORY_TYPE ||
            card.card.card["@type"] === WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE
        ) {
            return card.card.card.categoryId === categoryId;
        } else {
            return true;
        }
    });
};

export const filterCategoryByParentType = (cards: Array<any>, parentType: string) => {
    if (isEmpty(parentType)) {
        return cards;
    }
    return cards.map((cardObj) => {
        const { card } = cardObj;
        const { card: innerCard } = card;

        if (innerCard && innerCard["@type"] === WIDGET_TYPES.MENU_CATEGORY_GRID) {
            return {
                ...cardObj,
                card: {
                    ...card,
                    card: {
                        ...innerCard,
                        categoryList: innerCard.categoryList.filter(
                            (menuCard: IMenuCategoryInfo) => menuCard.parentType === parentType,
                        ),
                    },
                },
            };
        }

        return cardObj; // Return as is if condition doesn't meet
    });
};

/**
 * Helper function to get card data by type
 * @param cards - Array of card objects
 * @param type - The type of card to find (e.g., "type.googleapis.com/swiggy.gandalf.widgets.v2.Pills")
 * @returns The card data matching the type or null if not found
 */
export const getCardDataByType = (cards: Array<any>, type: string): Array<any> | null => {
    for (const cardObj of cards) {
        const card = cardObj?.card?.card;
        if (card && card["@type"] === type) {
            return card;
        }
    }
    return null; // Return null if no matching card is found
};

export const updateMenuUI = (
    updatedTransformedMenuResponse: any,
    selectedTab: string,
    dispatch: Dispatch<IQrCodeAction>,
) => {
    dispatch({
        type: QrCodeActions.UPDATE_INITIAL_MENU,
        payload: {
            data: updatedTransformedMenuResponse,
        },
    });

    const menuDataAfterParentCategory = filterByParentType(updatedTransformedMenuResponse, selectedTab);
    const updatedData = filterCategoryByParentType(menuDataAfterParentCategory, selectedTab);
    dispatch({
        type: QrCodeActions.UPDATE_DATA_BY_PARENT_TYPE,
        payload: {
            filteredData: updatedData,
            parentType: selectedTab,
            emptyItemCard: updatedData.length > 0 && isCollectionEmpty(updatedData),
        },
    });
};

export const ingestPinInMenuResponse = (
    updatedTransformedMenuResponse: any,
    response: {
        selectedTab: string;
        pin: string;
        tableNo: string;
        status: PinGenerationStatus;
        userStatus?: string;
    },
    dispatch: Dispatch<IQrCodeAction>,
) => {
    const { selectedTab = "", pin = "", tableNo = "", status = "", userStatus = "" } = response;
    const mastheadCard = updatedTransformedMenuResponse?.find(
        (card: { card: { card: { [x: string]: WidgetTypes } } }) => {
            if (card?.card?.card?.["@type"] === WidgetTypes.DINEOUT_MENU_HEADER) return card;
        },
    );
    if (!isEmpty(mastheadCard)) {
        if (!isEmpty(tableNo)) {
            mastheadCard.card.card.info.tableEntityAttributes = {
                label: "Table no",
                value: tableNo,
            };
        }

        const isHostUser =
            status === PinGenerationStatus.PIN_ALREADY_GENERATED ||
            status === PinGenerationStatus.SUCCESS ||
            status === PinGenerationStatus.USER_VERIFIED;
        const isVerifiedNonHost =
            status === PinGenerationStatus.PIN_GENERATED_BY_OTHER_USER &&
            userStatus === PinGenerationStatus.USER_VERIFIED;

        if (isHostUser || isVerifiedNonHost) {
            if (!isEmpty(pin)) {
                mastheadCard.card.card.info.pinEntityAttributes = {
                    label: "PIN",
                    value: pin,
                };
            }
        }

        updateMenuUI(deepClone(updatedTransformedMenuResponse), selectedTab, dispatch);
    }
};

// eslint-disable-next-line no-undef
let debounceTimeout: NodeJS.Timeout;

export const debounce = (func: (...args: any[]) => Promise<void>, delay: number) => {
    return function (...args: any[]) {
        return new Promise<void>((resolve, reject) => {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(async () => {
                try {
                    await func(...args);
                    resolve();
                } catch (error) {
                    reject(error);
                }
            }, delay);
        });
    };
};

export const isMenuRoute = (pathname: string) => pathname === ROUTES.MENU || pathname.startsWith(`${ROUTES.MENU}/`);

/**
 * Gets the width of an element by its ID.
 * Returns a fallback width if the element isn't mounted yet to prevent incorrect toast positioning.
 * @param elementId - The ID of the element to find
 * @returns The element width plus default horizontal margin, or fallback width if element not found
 */
export const getWidthByElementId = (elementId: string): number => {
    const defaultWidth = 0;
    if (typeof window === "undefined" || typeof document === "undefined") {
        // SSR fallback: return default size
        return defaultWidth;
    }

    const element = document.getElementById(elementId);
    if (element && element.offsetWidth > 0) {
        return element.offsetWidth + DELAYED_TOAST_CONSTANTS.DEFAULT_HORIZONTAL_MARGIN;
    }

    // Element not found or has no width, use fallback to prevent incorrect toast positioning
    return defaultWidth;
};

/**
 * Waits for FAB widget to load and animation to complete, then calls callback. Returns cleanup function.
 */
export const waitForFABWidget = (
    callback: () => void,
    options?: {
        maxWaitTime?: number;
        animationDuration?: number;
        fallbackTimeout?: number;
    },
): (() => void) => {
    const maxWaitTime = options?.maxWaitTime ?? DELAYED_TOAST_CONSTANTS.FAB_WIDGET_MAX_WAIT_TIME;
    const animationDuration = options?.animationDuration ?? DELAYED_TOAST_CONSTANTS.FAB_WIDGET_ANIMATION_DURATION;
    const fallbackTimeout = options?.fallbackTimeout ?? DELAYED_TOAST_CONSTANTS.FAB_WIDGET_FALLBACK_TIMEOUT;

    if (typeof window === "undefined" || typeof document === "undefined") {
        // SSR fallback: wait for animation duration then call
        const timeoutId = setTimeout(callback, animationDuration);
        return () => clearTimeout(timeoutId);
    }

    let observer: MutationObserver | null = null;
    let detectionTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    let animationTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    let isResolved = false;

    const cleanup = () => {
        if (!isEmpty(observer)) {
            observer.disconnect();
            observer = null;
        }
        if (!isEmpty(detectionTimeoutId)) {
            clearTimeout(detectionTimeoutId);
            detectionTimeoutId = undefined;
        }
        if (!isEmpty(animationTimeoutId)) {
            clearTimeout(animationTimeoutId);
            animationTimeoutId = undefined;
        }
        if (!isEmpty(fallbackTimeoutId)) {
            clearTimeout(fallbackTimeoutId);
            fallbackTimeoutId = undefined;
        }
    };

    const executeCallback = () => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        callback();
    };

    const startAnimationWait = () => {
        // Element detected, now wait for animation to complete
        if (isResolved) return;
        // Clear fallback timeout since we found the element
        if (fallbackTimeoutId) {
            clearTimeout(fallbackTimeoutId);
            fallbackTimeoutId = undefined;
        }
        animationTimeoutId = setTimeout(executeCallback, animationDuration);
    };

    // Set up absolute fallback timeout to ensure toast always shows
    // This ensures the toast appears even if widget never loads
    fallbackTimeoutId = setTimeout(() => {
        if (!isResolved) {
            executeCallback();
        }
    }, fallbackTimeout);

    // Check if element already exists
    const element = document.getElementById(DELAYED_TOAST_IDS.BUTTON_ID);
    if (element) {
        // Element already exists, wait for animation
        startAnimationWait();
        return cleanup;
    }

    // Set up detection timeout (if element doesn't appear within maxWaitTime, show toast anyway)
    detectionTimeoutId = setTimeout(() => {
        if (!isResolved) {
            // Element didn't appear in maxWaitTime, show toast without waiting for animation
            // since widget is not coming
            executeCallback();
        }
    }, maxWaitTime);

    // Set up MutationObserver to watch for the element
    if (typeof MutationObserver !== "undefined") {
        observer = new MutationObserver(() => {
            const fabElement = document.getElementById(DELAYED_TOAST_IDS.BUTTON_ID);
            if (fabElement && !isResolved) {
                // Clear detection timeout since we found the element
                if (detectionTimeoutId) {
                    clearTimeout(detectionTimeoutId);
                    detectionTimeoutId = undefined;
                }
                // Disconnect observer since we found the element
                if (observer) {
                    observer.disconnect();
                    observer = null;
                }
                // Wait for animation to complete
                startAnimationWait();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    return cleanup;
};

export const handleOrderToast = ({
    partnerOrderToast,
    showMenuNudge,
    isCartItemAvailable,
    onBillSyncApiReceived,
}: HandleOrderToastParams) => {
    if (isEmpty(partnerOrderToast)) {
        return;
    }

    if (!onBillSyncApiReceived) {
        return;
    }

    const bottomHeight = getToastBottomHeightMenuPage(isCartItemAvailable);
    const fabWidgetWidth = getWidthByElementId(DELAYED_TOAST_IDS.BUTTON_ID);
    const toastMargin = 2 * DELAYED_TOAST_CONSTANTS.DEFAULT_HORIZONTAL_MARGIN + fabWidgetWidth;

    const toastWidth = getToastWidth(toastMargin);
    partnerOrderToast.style = getToastStyle(bottomHeight, toastWidth) as CSSProperties;
    partnerOrderToast.progressColor = DELAYED_TOAST_CONSTANTS.PROGRESS_COLOR;

    onBillSyncApiReceived(partnerOrderToast, showMenuNudge);
};
